import React from "react";
import { useLocation } from "react-router-dom";
import { Box, IconButton, Link, List, Typography } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LanguageIcon from "@mui/icons-material/Language";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SidebarItem from "./SidebarItem";
import { PRIMARY_ITEMS, resolvePrimaryItem } from "./navConfig";

const PRIMARY_WIDTH_EXPANDED = 240;
const PRIMARY_WIDTH_COLLAPSED = 70;

const PrimarySidebar = ({
    isCollapsed,
    onToggle,
    activePrimaryId,
    onPrimaryClick,
    userRolesArray,
    isCreditBased,
    onLogout,
    isMobile,
    onLinkClick,
}) => {
    const showItem = (item) => {
        if (item.billingVisibility === "credit" && !isCreditBased) return false;
        if (item.billingVisibility === "fixed" && isCreditBased) return false;
        if (item.roleCheck) return item.roleCheck(userRolesArray);
        if (!item.roles) return true;
        if (Array.isArray(item.roles)) {
            return item.roles.some((r) => userRolesArray.includes(r));
        }
        return false;
    };

    const visibleItems = PRIMARY_ITEMS.filter(showItem).map((item) => resolvePrimaryItem(item, isCreditBased));
    const location = useLocation();

    const isPathActive = (path) => path && location.pathname === path;

    return (
        <Box
            sx={{
                width: isCollapsed ? PRIMARY_WIDTH_COLLAPSED : PRIMARY_WIDTH_EXPANDED,
                minWidth: isCollapsed ? PRIMARY_WIDTH_COLLAPSED : PRIMARY_WIDTH_EXPANDED,
                alignSelf: "stretch",
                minHeight: 0,
                backgroundColor: "primary.main",
                display: "flex",
                flexDirection: "column",
                transition: "width 250ms cubic-bezier(0.4, 0, 0.2, 1)",
                overflow: "hidden",
                flexShrink: 0,
            }}
        >
            {/* Header / Toggle - matches main header primary color */}
            <Box
                sx={{
                    height: 64,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isCollapsed ? "center" : "space-between",
                    px: isCollapsed ? 0 : 2,
                    backgroundColor: "primary.main",
                }}
            >
                {!isCollapsed && (
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: "white" }} fontSize="0.8rem">
                        Menu
                    </Typography>
                )}
                {!isMobile && (
                    <IconButton
                        onClick={onToggle}
                        size="small"
                        sx={{ color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
                    >
                        {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                    </IconButton>
                )}
            </Box>

            {/* Nav Items - scrollbar hidden */}
            <List
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    py: 1,
                    px: 0,
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {visibleItems.map((item) => {
                    if (item.action === "logout") {
                        return (
                            <SidebarItem
                                key={item.id}
                                icon={item.icon}
                                label={item.name}
                                isActive={false}
                                isCollapsed={isCollapsed}
                                onClick={onLogout}
                            />
                        );
                    }
                    if (item.child) {
                        return (
                            <SidebarItem
                                key={item.id}
                                icon={item.icon}
                                label={item.name}
                                isActive={activePrimaryId === item.id}
                                isCollapsed={isCollapsed}
                                onClick={() => onPrimaryClick(item)}
                            />
                        );
                    }
                    return (
                        <SidebarItem
                            key={item.id}
                            icon={item.icon}
                            label={item.name}
                            isActive={isPathActive(item.path) && !activePrimaryId}
                            isCollapsed={isCollapsed}
                            href={item.path}
                            component="a"
                            onClick={onLinkClick}
                        />
                    );
                })}
            </List>

            {/* Footer CTA */}
            {!isCollapsed && (
                <Box
                    sx={{
                        flexShrink: 0,
                        px: 2,
                        py: 2,
                        pt: 1.5,
                        borderTop: "1px solid rgba(255,255,255,0.2)",
                        background: "linear-gradient(to top, rgba(0,0,0,0.08), transparent)",
                    }}
                >
                    <Typography
                        sx={{
                            color: "rgba(255,255,255,0.95)",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            mb: 1.5,
                            letterSpacing: "0.3px",
                        }}
                    >
                        Varga Works support and services
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.25 }}>
                        <Link
                            href="tel:+918368109855"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "rgba(255,255,255,0.9)",
                                fontSize: "0.8rem",
                                textDecoration: "none",
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.12)",
                                },
                            }}
                        >
                            <PhoneIcon sx={{ fontSize: 14, opacity: 0.9 }} />
                            +91-8368109855
                        </Link>
                        <Link
                            href="mailto:vargaworks.official@gmail.com"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "rgba(255,255,255,0.9)",
                                fontSize: "0.75rem",
                                textDecoration: "none",
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                                wordBreak: "break-all",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.12)",
                                },
                            }}
                        >
                            <EmailIcon sx={{ fontSize: 14, opacity: 0.9, flexShrink: 0 }} />
                            vargaworks.official@gmail.com
                        </Link>
                    </Box>
                    <Link
                        href="https://vargaworks.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: "rgba(255,255,255,0.95)",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            textDecoration: "none",
                            borderRadius: 1,
                            px: 1,
                            py: 0.5,
                            transition: "all 0.2s ease",
                            "&:hover": {
                                color: "white",
                                bgcolor: "rgba(255,255,255,0.12)",
                            },
                        }}
                    >
                        <LanguageIcon sx={{ fontSize: 14, opacity: 0.9 }} />
                        vargaworks.com
                    </Link>
                </Box>
            )}
        </Box>
    );
};

export default PrimarySidebar;
export { PRIMARY_WIDTH_EXPANDED, PRIMARY_WIDTH_COLLAPSED };
